import { useRouter, useRoute } from 'vue-router'
import { useTabStore } from '@/stores/tabs'

/**
 * Composable for opt-in tab navigation.
 * Normal navigation works as before (single view).
 * Call openInNewTab() to explicitly open something in a new tab.
 */
export function useTabNavigation() {
  const router = useRouter()
  const route = useRoute()
  const tabStore = useTabStore()

  /**
   * Open a path in a new tab. Always creates a new tab even if
   * the same path is already open (duplicate tabs supported).
   * If no tabs exist yet, the current page becomes the first tab.
   */
  function openInNewTab(path: string, title: string) {
    // If this is the first tab, pin the current page as tab #1
    if (tabStore.tabCount === 0) {
      const currentTitle = buildTitle(route)
      tabStore.addTab(route.path, currentTitle)
    }

    // Add the new tab (allow duplicates) and navigate to it
    tabStore.addTab(path, title, { duplicate: true })
    router.push(path)
  }

  return { openInNewTab }
}

/** Build a display title from a route */
function buildTitle(r: { meta?: Record<string, unknown>; name?: string | symbol | null | undefined; params?: Record<string, string | string[]> }): string {
  let title = (r.meta?.title as string) || (typeof r.name === 'string' ? r.name : '') || 'Page'
  title = title.replace(/([a-z])([A-Z])/g, '$1 $2')
  if (r.params && 'id' in r.params) title = `${title} #${r.params.id}`
  return title
}
